<template>
	<v-container fluid>
		<section>
			<!-- Summary cards -->
			<v-row class="mb-3">
				<template v-if="summaries.length">
					<v-col v-for="(item, index) in summaries" :key="index" cols="12" sm="4">
						<v-card class="app-summary-card" rounded="lg" outlined>
							<v-card-text class="pa-5">
								<v-list-item class="px-0">
									<v-list-item-avatar class="rounded-lg app-summary-icon" size="48">
										<v-icon color="primary" small>
											{{ item.icon }}
										</v-icon>
									</v-list-item-avatar>
									<v-list-item-content class="py-0">
										<v-list-item-subtitle class="text-body-2 mb-1">
											{{ $t(item.title) }}
										</v-list-item-subtitle>
										<v-list-item-title class="text-h5 font-weight-bold text-primary">
											{{ formatSummaryValue(item) }}
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-card-text>
						</v-card>
					</v-col>
				</template>
				<template v-else>
					<v-col v-for="(item, index) in 3" :key="index" cols="12" sm="4">
						<v-card rounded class="rounded-lg">
							<v-card-text class="pa-5">
								<v-skeleton-loader type="list-item-avatar-three-line" />
							</v-card-text>
						</v-card>
					</v-col>
				</template>
			</v-row>

			<!-- Compact Filter Section -->
			<v-card class="mb-3" outlined>
				<v-card-text class="py-3 px-4">
					<div class="text-left">
						<h6>{{ $t('filter') }}</h6>
					</div>
					<!-- Main Filter Row -->
					<v-row align="center">
						<!-- Period Filter -->
						<v-col cols="12" sm="6" md="2" lg="2">
							<TextSelect
								:dense="true"
								:placeholder="false"
								:items="periodItems"
								label="period"
								itemValue="value"
								text="name"
								v-model="period"
								hide-details
							/>
						</v-col>

						<!-- Start Date -->
						<v-col cols="12" sm="6" md="2" lg="2">
							<DatePicker :max="date_to" :dense="true" :placeholder="false" label="start_date" v-model="date_from" hide-details />
						</v-col>

						<!-- End Date -->
						<v-col cols="12" sm="6" md="2" lg="2">
							<DatePicker :min="date_from" :dense="true" :placeholder="false" label="end_date" v-model="date_to" hide-details />
						</v-col>

						<!-- Search Field -->
						<v-col cols="12" sm="6" md="3" lg="3">
							<TextSearch
								v-model="search"
								@search="filterData()"
								@onClear="
									search = '';
									filterData();
								"
							/>
						</v-col>

						<!-- Action Buttons -->
						<v-col cols="12" sm="6" md="3" lg="3">
							<div class="d-flex align-center justify-end gap-2">
								<v-btn depressed color="primary" @click="newItem">
									<v-icon left small>fal fa-plus-circle</v-icon>
									{{ $t('cash_in_out') }}
								</v-btn>
							</div>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>

			<!-- Data Table -->
			<data-table
				v-if="cashTransactions && cashTransactions.data"
				:headers="headers"
				:items="cashTransactions"
				commit="SET_CASH_TRANSACTIONS"
				:customActions="rowActions"
				customActionsAsIcons
			>
				<div slot="activator" class="row">
					<div class="col-12">
						<v-card-title class="pa-0">{{ $t('cash_management') }}</v-card-title>
					</div>
				</div>
			</data-table>

			<!-- View dialog -->
			<app-custom-dialog v-model="dialog" :footer="false" title="description">
				<div slot="activator">
					<v-row>
						<v-col cols="4">{{ $t('id') }}</v-col>
						<v-col cols="8">{{ form.id }}</v-col>
						<v-col cols="4">{{ $t('type') }}</v-col>
						<v-col cols="8">
							<v-chip label :color="form.type === 'cash_in' ? 'green' : 'red'" small class="white--text">
								{{ $t(form.type) }}
							</v-chip>
						</v-col>
						<v-col cols="4">{{ $t('reason') }}</v-col>
						<v-col cols="8">{{ form.reason }}</v-col>
						<v-col cols="4">{{ $t('amount') }}</v-col>
						<v-col cols="8">{{ form.amount | NumberFormat }}</v-col>
						<v-col cols="4">{{ $t('branch') }}</v-col>
						<v-col cols="8">{{ form.branch && form.branch.name }}</v-col>
						<v-col cols="4">{{ $t('user') || 'User' }}</v-col>
						<v-col cols="8">{{ form.user && form.user.first_name }}</v-col>
						<v-col cols="4">{{ $t('attach_file') }}</v-col>
						<v-col cols="8">
							<a v-if="form.file_path" :href="fileHref(form.file_path)" target="_blank" rel="noopener">
								{{ $t('view') || 'View' }}
							</a>
							<span v-else>—</span>
						</v-col>
						<v-col cols="4">{{ $t('date') }}</v-col>
						<v-col cols="8">{{ form.created_at | moment }}</v-col>
					</v-row>
				</div>
			</app-custom-dialog>

			<!-- Add/Edit dialog -->
			<app-custom-dialog v-model="dialogAdd" :footer="true" title="new_data" :disable-submit="cashOutBlocked" @submit="submitForm">
				<div slot="activator">
					<v-row>
						<v-col cols="12">
							<v-tabs
								v-model="form.type"
								grow
								height="50"
								:slider-color="form.type === 'cash_in' ? 'green' : 'red'"
								:data-cash-type="form.type"
								@change="$v.form.type.$touch()"
							>
								<v-tab value="cash_in" class="cash-in-tab">
									<v-icon left small>fal fa-arrow-down</v-icon>
									{{ $t('cash_in') }}
								</v-tab>
								<v-tab value="cash_out" class="cash-out-tab">
									<v-icon left small>fal fa-arrow-up</v-icon>
									{{ $t('cash_out') }}
								</v-tab>
							</v-tabs>
							<div v-if="typeError && typeError.length > 0" class="error--text text-caption mt-1">
								{{ typeError[0] }}
							</div>
							<ShiftLockoutAlert class="mt-3" :show="isCashOutTab" @clock-in-requested="showClockInDialog = true" />
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12">
							<CurrencyInput
								label="amount"
								:errorMessages="amountError"
								v-model="form.amount"
								@input="$v.form.amount.$touch()"
								@blur="$v.form.amount.$touch()"
							/>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12">
							<TextArea label="note_optional" v-model="form.reason" :rows="3" outlined dense />
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12">
							<div class="mb-2">
								<div class="text-caption grey--text mb-1">
									{{ $t('attach_file') }}
								</div>
								<DragDropUpload
									v-model="form.file"
									accept="image/*,.pdf"
									:max-size-mb="5"
									@file-selected="onFileSelected"
									@file-removed="onFileRemoved"
								/>
							</div>
						</v-col>
					</v-row>
				</div>
			</app-custom-dialog>

			<!-- Clock In Dialog: offered by ShiftLockoutAlert when cash_out is picked with no open shift -->
			<ClockInDialog v-model="showClockInDialog" @success="handleClockInSuccess" />

			<app-loading :overlay="overlay" />
		</section>
	</v-container>
</template>

<script>
import DataTable from '@/components/DataTable';
import { mapGetters, mapActions } from 'vuex';
import { cashTransactionHead } from '@/utils/head';
import TextSelect from '@/components/global/TextSelect';
import { myMixin } from '@/mixin/main';
import { cashTransactionValidate } from '@/validations/shop';
import { success, confirm, error } from '@/controllers/alert';
import CurrencyInput from '@/components/CurrencyInput';
import DragDropUpload from '@/components/global/DragDropUpload.vue';
import TextArea from '@/components/global/TextArea';
import { getCashDrawerService } from '@/services/cashDrawerService';
import { buildRowActions, isCancellable, apiErrorMessage } from '@/utils/cashTransactionActions';
import ShiftLockoutAlert from '@/components/shift/ShiftLockoutAlert';
import ClockInDialog from '@/pages/shift-management/ClockInDialog';

export default {
	mixins: [myMixin, cashTransactionValidate],
	data: () => ({
		dialogAdd: false,
		dialog: false,
		overlay: false,
		showClockInDialog: false,
		search: '',
		period: 'monthly',
		date_from: new Date().toISOString().substr(0, 10),
		date_to: new Date().toISOString().substr(0, 10),
		summaryTotals: {},
		periodItems: [
			{ name: 'Daily', value: 'daily' },
			{ name: 'Weekly', value: 'weekly' },
			{ name: 'Monthly', value: 'monthly' },
		],
		typeItems: [
			{ name: 'Cash In', value: 'cash_in' },
			{ name: 'Cash Out', value: 'cash_out' },
		],
		form: {
			type: 'cash_in',
			reason: '',
			amount: null,
			file: null,
			file_path: null,
		},
	}),
	async created() {
		await this.fetchItem();
		await this.getSummary();
		// Auto-reconnect to previously paired cash drawer on component mount
		// Silent reconnect - don't show errors if no drawer is available
		const cashDrawerService = getCashDrawerService();
		if (cashDrawerService.isSupported()) {
			try {
				await cashDrawerService.reconnect();
				// Log success silently, but don't show errors if no device found
				// This handles stores without cash drawers gracefully
			} catch (error) {
				// Silently ignore - store may not have cash drawer
				console.log('[CashManagement] No previously paired cash drawer found');
			}
		}
	},
	components: {
		DataTable,
		CurrencyInput,
		TextSelect,
		DragDropUpload,
		TextArea,
		ShiftLockoutAlert,
		ClockInDialog,
	},
	computed: {
		...mapGetters({
			cashTransactions: 'index/cashTransactions',
		}),
		// Vuetify's v-tabs here binds form.type to the clicked tab's numeric index
		// (0/1), not the "cash_in"/"cash_out" string the v-tab value attributes
		// suggest — only the initial data() value and newItem()'s reset are ever
		// the string form. The backend's normalizeType() already tolerates both
		// (0/'0'/1/'1' alongside the strings), so this mirrors that leniency
		// instead of "fixing" the binding and risking the submit payload.
		isCashOutTab() {
			const type = this.form.type;
			return type === 'cash_out' || type === 1 || type === '1';
		},
		// Only cash_out requires an open shift server-side (CashTransactionController::
		// validateCashOutAmount); cash_in has no such check, so the submit button only
		// locks for the cash_out tab (QRIPO-488 — the old 404 surfaced only after submit).
		cashOutBlocked() {
			return this.isCashOutTab && !this.$store.getters['index/activeShift'];
		},
		headers() {
			return cashTransactionHead(this);
		},
		summaries() {
			return [
				{
					title: 'cash_in_total',
					icon: 'fal fa-arrow-down',
					color: 'green',
					textColor: 'green--text',
					total: this.summaryTotals.total_cash_in || 0,
				},
				{
					title: 'cash_out_total',
					icon: 'fal fa-arrow-up',
					color: 'red',
					textColor: 'red--text',
					total: this.summaryTotals.total_cash_out || 0,
				},
				{
					title: 'net',
					icon: 'fal fa-balance-scale',
					color: (this.summaryTotals.net || 0) >= 0 ? 'green' : 'red',
					textColor: (this.summaryTotals.net || 0) >= 0 ? 'green--text' : 'red--text',
					total: this.summaryTotals.net || 0,
				},
			];
		},
	},
	methods: {
		...mapActions({
			fetch: 'index/fetch',
			fetchActiveShift: 'index/fetchActiveShift',
		}),
		async handleClockInSuccess() {
			await this.fetchActiveShift(this);
			this.showClockInDialog = false;
		},
		fileHref(path) {
			if (!path) return '#';
			if (path.startsWith('http')) return path;
			const base = (process.env.VUE_APP_BASE_URL || '').replace(/\/api\/?$/, '');
			return (base || window.location.origin) + '/uploads/' + path;
		},
		resetFormFiles() {
			this.form.file = null;
			this.form.file_path = null;
		},
		onFileSelected() {},
		onFileRemoved() {},
		// Format summary value
		formatSummaryValue(item) {
			return this.$options.filters.NumberFormat(item.total);
		},
		// Filter data method
		async filterData() {
			await this.fetchItem();
		},
		buildPaginateUrl() {
			let u = `cash_transaction_paginate?q=${encodeURIComponent(this.search || '')}`;
			u += `&date_from=${this.date_from || ''}&date_to=${this.date_to || ''}`;
			return u;
		},
		async fetchItem() {
			const body = {
				that: this,
				url: this.buildPaginateUrl(),
				commit: 'SET_CASH_TRANSACTIONS',
			};
			await this.fetch(body);
		},
		async getSummary() {
			const url = `cash_transaction_summary?period=${this.period}&date_from=${this.date_from || ''}&date_to=${this.date_to || ''}`;
			try {
				const { data } = await this.$http.get(url);
				if (data.status && data.data && data.data.totals) {
					this.summaryTotals = data.data.totals;
				}
			} catch (e) {
				this.summaryTotals = {};
			}
		},
		applyFilter() {
			this.fetchItem();
			this.getSummary();
		},
		async newItem() {
			await Help.clearForm(this.form, this);
			this.form.type = 'cash_in';
			this.resetFormFiles();
			this.dialogAdd = true;
		},
		/**
		 * Cash transactions are an audit trail, so there is no edit and no delete:
		 * the row stays as recorded and a mistake is corrected by a reversing entry
		 * (see cancelItem). The backend rejects PUT/DELETE outright — hiding the
		 * buttons alone would have left the API open.
		 */
		rowActions(item) {
			return buildRowActions({
				item,
				activeShift: this.$store.getters['index/activeShift'],
				t: (key) => this.$t(key),
				onView: this.viewItem,
				onCancel: this.cancelItem,
			});
		},
		isCancellable(item) {
			return isCancellable(item, this.$store.getters['index/activeShift']);
		},
		/**
		 * Report a failed request to the user. The axios interceptor already alerts
		 * on most statuses but passes 404 straight through, so anything the
		 * component catches itself has to be surfaced here or it vanishes.
		 */
		async showApiError(err, context) {
			console.error(context, err);
			const key = apiErrorMessage(err);
			if (key) await error(this, key);
		},
		async cancelItem(item) {
			const { isConfirmed } = await confirm({
				props: this,
				text: 'want_to_cancel_transaction',
			});
			if (!isConfirmed) return;
			try {
				const { data } = await this.$http.post(`cash_transaction/${item.id}/cancel`);
				if (data.status) {
					await this.fetchItem();
					await this.getSummary();
					await success(this, data.message);
				}
			} catch (err) {
				await this.showApiError(err, 'cancelItem');
			}
		},
		viewItem(item) {
			this.form = { ...item };
			this.dialog = true;
		},
		/**
		 * Open cash drawer using WebUSB
		 * Works with USB-connected thermal printers (XP-80, Epson, Star Micronics, etc.)
		 * Non-blocking - failures won't prevent transaction completion
		 * Gracefully handles stores without cash drawers
		 */
		async openCashDrawerIfAvailable() {
			try {
				const cashDrawerService = getCashDrawerService();

				// Check if WebUSB is supported
				if (!cashDrawerService.isSupported()) {
					// Silently return - store may not use cash drawer or browser doesn't support it
					console.log('[CashManagement] WebUSB not supported. Skipping cash drawer (store may not use cash drawer).');
					return;
				}

				// Check if device is paired
				const status = cashDrawerService.getStatus();
				if (!status.paired) {
					// No paired device - store likely doesn't use cash drawer
					console.log('[CashManagement] No cash drawer paired. Store may not use cash drawer.');
					return;
				}

				// Attempt to open the drawer
				console.log('[CashManagement] Attempting to open cash drawer...');
				const success = await cashDrawerService.openDrawer();

				if (success) {
					console.log('[CashManagement] Cash drawer opened successfully');
				} else {
					const drawerStatus = cashDrawerService.getStatus();
					// Log warning but don't show to user - transaction already completed
					console.warn('[CashManagement] Cash drawer failed to open:', drawerStatus.message || 'Unknown error');
					// This is expected if store doesn't have cash drawer or device is disconnected
				}
			} catch (error) {
				// Silently handle errors - store may not have cash drawer
				console.log('[CashManagement] Cash drawer operation skipped:', error.message || 'Store may not use cash drawer');
				// Don't throw - cash drawer failure shouldn't block transaction
			}
		},
		async submitForm() {
			this.$v.form.$touch();
			if (this.$v.form.$invalid) return;
			try {
				const hasFile = this.form.file && this.form.file instanceof File;
				// Creation is the only write left: an existing row is never edited, only
				// cancelled by a reversing entry (see cancelItem).
				const activeShift = this.$store.getters['index/activeShift'];

				let payload;
				if (hasFile) {
					payload = new FormData();
					payload.append('type', this.form.type);
					payload.append('reason', this.form.reason);
					payload.append('amount', this.form.amount);
					payload.append('file', this.form.file);
					if (activeShift) payload.append('shift_id', activeShift.id);
				} else {
					payload = {
						type: this.form.type,
						reason: this.form.reason,
						amount: this.form.amount,
					};
					if (activeShift) payload.shift_id = activeShift.id;
				}

				const { data } = await this.$http.post('cash_transaction', payload);
				if (data.status) {
					this.dialogAdd = false;
					await Help.clearForm(this.form, this);
					this.resetFormFiles();
					await this.fetchItem();
					await this.getSummary();
					await success(this, data.message);
					await this.openCashDrawerIfAvailable();
				}
			} catch (err) {
				// A cash_out with no open shift aborts 404 active_shift_not_found, and
				// the axios interceptor deliberately leaves 404s to the component
				// ("let component handle with toast"). Swallowing it here is what made
				// the submit button look dead: the request went out, failed, and the
				// dialog just sat there.
				await this.showApiError(err, 'submitForm');
			}
		},
	},
	watch: {
		date_from() {
			this.applyFilter();
		},
		date_to() {
			this.applyFilter();
		},
		period() {
			this.applyFilter();
		},
	},
};
</script>

<style scoped>
/* Gap utility for flex containers */
.gap-2 {
	gap: 8px;
}

/* Cash In/Out Tabs Styling */
.cash-in-tab.v-tab--active {
	color: #4caf50 !important;
}

.cash-in-tab.v-tab--active .v-icon {
	color: #4caf50 !important;
}

.cash-out-tab.v-tab--active {
	color: #f44336 !important;
}

.cash-out-tab.v-tab--active .v-icon {
	color: #f44336 !important;
}

/* Slider color based on selected tab */
.v-tabs:has(.cash-in-tab.v-tab--active) .v-tabs-slider {
	background-color: #4caf50 !important;
}

.v-tabs:has(.cash-out-tab.v-tab--active) .v-tabs-slider {
	background-color: #f44336 !important;
}

/* Fallback for browsers that don't support :has() */
.v-tabs[data-cash-type='cash_in'] .v-tabs-slider {
	background-color: #4caf50 !important;
}

.v-tabs[data-cash-type='cash_out'] .v-tabs-slider {
	background-color: #f44336 !important;
}

/* Touch-friendly tab sizing */
.cash-in-tab,
.cash-out-tab {
	min-height: 50px;
	font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 600px) {
	.cash-in-tab,
	.cash-out-tab {
		font-size: 14px;
		padding: 12px 8px;
	}

	.cash-in-tab .v-icon,
	.cash-out-tab .v-icon {
		font-size: 16px;
	}
}
</style>
